import { ScraperEntry } from './importScraper';
import { generateRestoSlug } from './RestoSlugUtils';
import { isBrowser } from './isServer';

interface Address {
  "@type": "PostalAddress";
  streetAddress?: string;
  addressLocality?: string;
  addressRegion?: string;
  postalCode?: string;
  addressCountry: string;
}

interface GeoCoordinates {
  "@type": "GeoCoordinates";
  latitude: number;
  longitude: number;
}

interface AggregateRating {
  "@type": "AggregateRating";
  ratingValue: string;
  reviewCount: string;
  bestRating: string;
  worstRating: string;
  ratingExplanation?: string;
}

interface RestaurantSchema {
  "@context": "https://schema.org";
  "@type": "Restaurant";
  name: string;
  description?: string;
  url?: string;
  address?: Address;
  geo?: GeoCoordinates;
  aggregateRating?: AggregateRating;
  servesCuisine?: string;
  priceRange?: string;
  telephone?: string;
  image?: string;
  openingHours?: string[];
  acceptsReservations?: boolean;
  hasMenu?: string;
  additionalProperty?: Array<{
    "@type": "PropertyValue";
    name: string;
    value: string;
  }>;
}

interface WebSiteSchema {
  "@context": "https://schema.org";
  "@type": "WebSite";
  name: string;
  url: string;
  description: string;
  potentialAction?: {
    "@type": "SearchAction";
    target: string;
    "query-input": string;
  };
  publisher?: {
    "@type": "Organization";
    name: string;
    url: string;
  };
}

interface OrganizationSchema {
  "@context": "https://schema.org";
  "@type": "Organization";
  name: string;
  url: string;
  description: string;
  foundingDate?: string;
  contactPoint?: {
    "@type": "ContactPoint";
    contactType: string;
    url: string;
  };
}

export class StructuredDataManager {
  private static readonly BASE_URL = 'https://kauwenofspauwen.be';
  private static readonly SITE_NAME = 'Kauwen of Spauwen';

  /**
   * Generate Restaurant structured data from ScraperEntry
   */
  static generateRestaurantSchema(entry: ScraperEntry): RestaurantSchema {
    const schema: RestaurantSchema = {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      name: entry.name,
      description: `Food safety information and inspection results for ${entry.name}. Current hygiene rating and inspection history available.`,
      url: `${this.BASE_URL}?restoId=${generateRestoSlug(entry.name, entry.id)}`
    };

    // Add address if available
    if (entry.address) {
      schema.address = this.parseAddress(entry.address);
    }

    // Add geo coordinates if available
    if (entry.latitude && entry.longitude) {
      schema.geo = {
        "@type": "GeoCoordinates",
        latitude: entry.latitude,
        longitude: entry.longitude
      };
    }

    // Add aggregate rating based on inspection data
    if (entry.inspectionForks || entry.inspectionSmiley) {
      schema.aggregateRating = this.generateRatingFromInspection(entry);
    }

    // Add image if available
    if (entry.image) {
      schema.image = entry.image;
    }

    // Add additional properties for food safety data
    schema.additionalProperty = this.generateAdditionalProperties(entry);

    return schema;
  }

  /**
   * Generate WebSite structured data for the homepage
   */
  static generateWebSiteSchema(): WebSiteSchema {
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: this.SITE_NAME,
      url: this.BASE_URL,
      description: "Restaurant food safety rating platform for Belgium. View hygiene inspection results, report food poisoning experiences, and find safe dining options.",
      potentialAction: {
        "@type": "SearchAction",
        target: `${this.BASE_URL}?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      },
      publisher: {
        "@type": "Organization",
        name: this.SITE_NAME,
        url: this.BASE_URL
      }
    };
  }

  /**
   * Generate Organization structured data
   */
  static generateOrganizationSchema(): OrganizationSchema {
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: this.SITE_NAME,
      url: this.BASE_URL,
      description: "Food safety rating platform providing restaurant hygiene inspection results and food poisoning reporting for Belgium.",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        url: this.BASE_URL
      }
    };
  }

  /**
   * Parse address string into structured address object
   */
  private static parseAddress(addressString: string): Address {
    const address: Address = {
      "@type": "PostalAddress",
      addressCountry: "BE" // Belgium
    };

    // Basic parsing - can be enhanced based on address format
    const parts = addressString.split(',').map(part => part.trim());
    
    if (parts.length > 0) {
      address.streetAddress = parts[0];
    }
    
    if (parts.length > 1) {
      // Try to extract postal code and city from the last part
      const lastPart = parts[parts.length - 1];
      const postalCodeMatch = lastPart.match(/(\d{4})\s+(.+)/);
      
      if (postalCodeMatch) {
        address.postalCode = postalCodeMatch[1];
        address.addressLocality = postalCodeMatch[2];
      } else {
        address.addressLocality = lastPart;
      }
    }

    return address;
  }

  /**
   * Generate rating from inspection data
   */
  private static generateRatingFromInspection(entry: ScraperEntry): AggregateRating {
    // Convert fork rating to numeric scale (assuming 0-5 scale)
    let ratingValue = "0";
    
    if (entry.inspectionForks != null) {
      ratingValue = String(entry.inspectionForks);
    }

    const rating: AggregateRating = {
      "@type": "AggregateRating",
      ratingValue: ratingValue,
      reviewCount: "1", // Based on inspection
      bestRating: "5",
      worstRating: "0"
    };

    // Add explanation based on smiley/inspection result
    if (entry.inspectionSmiley) {
      rating.ratingExplanation = `Food safety inspection result: ${entry.inspectionSmiley}`;
    }

    return rating;
  }

  /**
   * Generate additional properties for food safety data
   */
  private static generateAdditionalProperties(entry: ScraperEntry): Array<{
    "@type": "PropertyValue";
    name: string;
    value: string;
  }> {
    const properties: Array<{
      "@type": "PropertyValue";
      name: string;
      value: string;
    }> = [];

    if (entry.operatorNumber) {
      properties.push({
        "@type": "PropertyValue",
        name: "Operator Number",
        value: entry.operatorNumber
      });
    }

    if (entry.operatorType) {
      properties.push({
        "@type": "PropertyValue",
        name: "Operator Type",
        value: entry.operatorType
      });
    }

    if (entry.operatorStatus) {
      properties.push({
        "@type": "PropertyValue",
        name: "Operator Status",
        value: entry.operatorStatus
      });
    }

    if (entry.inspectionFrom) {
      properties.push({
        "@type": "PropertyValue",
        name: "Last Inspection Date",
        value: new Date(entry.inspectionFrom * 1000).toISOString().split('T')[0]
      });
    }

    if (entry.inspectionCode) {
      properties.push({
        "@type": "PropertyValue",
        name: "Inspection Code",
        value: entry.inspectionCode
      });
    }

    return properties;
  }

  /**
   * Inject structured data into the page
   */
  static injectStructuredData(data: object): void {
    if (!isBrowser) return;

    // Remove existing structured data script
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Create and inject new structured data script
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data, null, 2);
    document.head.appendChild(script);
  }

  /**
   * Generate and inject restaurant structured data
   */
  static injectRestaurantData(entry: ScraperEntry): void {
    const schema = this.generateRestaurantSchema(entry);
    this.injectStructuredData(schema);
  }

  /**
   * Generate and inject homepage structured data
   */
  static injectHomepageData(): void {
    const websiteSchema = this.generateWebSiteSchema();
    this.injectStructuredData(websiteSchema);
  }

  /**
   * Validate structured data (basic validation)
   */
  static validateSchema(schema: object): boolean {
    try {
      const schemaString = JSON.stringify(schema);
      const parsed = JSON.parse(schemaString);
      
      // Basic validation - check for required fields
      if (!parsed['@context'] || !parsed['@type']) {
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Schema validation error:', error);
      return false;
    }
  }
}

/**
 * Singleton instance for managing structured data
 */
class StructuredDataManagerInstance {
  private structuredDataMap: Map<string, object> = new Map();

  /**
   * Add structured data with a key
   */
  addStructuredData(key: string, data: object): void {
    this.structuredDataMap.set(key, data);
    this.updatePageStructuredData();
  }

  /**
   * Remove structured data by key
   */
  removeStructuredData(key: string): void {
    this.structuredDataMap.delete(key);
    this.updatePageStructuredData();
  }

  /**
   * Update the page with all current structured data
   */
  private updatePageStructuredData(): void {
    if (!isBrowser) return;

    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Add all current structured data
    this.structuredDataMap.forEach((data, key) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data, null, 2);
      script.setAttribute('data-key', key);
      document.head.appendChild(script);
    });
  }

  /**
   * Clear all structured data
   */
  clearAll(): void {
    this.structuredDataMap.clear();
    if (!isBrowser) return;
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());
  }
}

export const structuredDataManager = new StructuredDataManagerInstance();
export default StructuredDataManager;